import { useEffect } from 'react';
import { create } from 'zustand';

// ==============================|| PROMPTS — WHO'S SHOWING ||============================== //
// Prompts that cover the page register while on screen, so another prompt never opens on top of them.
// Prompts with their own store (checkout drawer, QuickSubscribe, the tour) are read from those stores.

export const PROMPTS = {
  getStartedLock: 'get-started-lock',
  eaFeedback: 'ea-feedback',
  announcement: 'announcement'
} as const;

export type PromptId = (typeof PROMPTS)[keyof typeof PROMPTS];

// A list, not a set: the same prompt can be mounted twice (EaFeedbackPrompt sits on two pages).
export const useShowingPrompts = create<{ showing: PromptId[] }>()(() => ({ showing: [] }));

const withoutOne = (list: PromptId[], prompt: PromptId): PromptId[] => {
  const i = list.indexOf(prompt);
  return i < 0 ? list : [...list.slice(0, i), ...list.slice(i + 1)];
};

/** Marks a prompt as on screen while `active`; unmounting clears it. */
export function useShowingPrompt(prompt: PromptId, active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    useShowingPrompts.setState((s) => ({ showing: [...s.showing, prompt] }));
    return () => useShowingPrompts.setState((s) => ({ showing: withoutOne(s.showing, prompt) }));
  }, [prompt, active]);
}

const othersShowing = (showing: PromptId[], self: PromptId): boolean => showing.some((p) => p !== self);

/** True while any prompt other than `self` is on screen. */
export const useOtherPromptShowing = (self: PromptId): boolean => useShowingPrompts((s) => othersShowing(s.showing, self));

/** The same, read once outside React (just before a prompt opens). */
export const isOtherPromptShowing = (self: PromptId): boolean => othersShowing(useShowingPrompts.getState().showing, self);
